import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/feature/MovieCard";
import { useSeo } from "@/hooks/useSeo";
import { movies } from "@/mocks/movies";
import { news } from "@/mocks/news";
import HeroCarousel from "./components/HeroCarousel";
import CategoryShortcuts from "./components/CategoryShortcuts";

export default function Home() {
  useSeo({
    title: "一本道影视大全-最新电影电视剧推荐与资讯",
    description:
      "一本道影视大全网站提供最新电影、电视剧及热门影视资讯，覆盖多种类型作品，方便用户查找影视内容，了解行业动态。",
    keywords: "一本道,影视大全,电影推荐,电视剧,影视资讯,影视作品,影视资源",
  });

  const featured = movies.filter((m) => m.featured).slice(0, 5);
  const latest = [...movies].sort((a, b) => b.year - a.year).slice(0, 8);
  const hot = [...movies].sort((a, b) => b.views - a.views).slice(0, 4);
  const latestNews = news.slice(0, 5);

  return (
    <SiteLayout>
      <HeroCarousel items={featured} />
      <CategoryShortcuts />

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="flex items-end justify-between gap-4 mb-6 md:mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">最新影视推荐</h2>
            <p className="mt-2 text-sm text-foreground-500">按上线时间为你精选近期值得一看的影视作品</p>
          </div>
          <Link
            to="/category?sort=latest"
            className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            查看全部
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {latest.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      <section className="w-full bg-background-100 border-y border-background-200">
        <div className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
            <div className="lg:col-span-2">
              <div className="flex items-end justify-between gap-4 mb-6">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">热门影视资讯</h2>
                  <p className="mt-2 text-sm text-foreground-500">行业动态、影视快讯，第一时间掌握</p>
                </div>
                <Link
                  to="/news"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  更多资讯
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="space-y-4">
                {latestNews.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/detail/${item.id}`}
                    className="group flex gap-4 p-3 md:p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="w-24 h-20 md:w-32 md:h-24 rounded-md overflow-hidden shrink-0 bg-background-200">
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={`${item.title} - 一本道影视大全`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-xs text-foreground-400">
                        <span className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-900">{item.category}</span>
                        <span>{item.publishedAt}</span>
                      </div>
                      <h3 className="mt-2 text-sm md:text-base font-bold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </h3>
                      <p className="mt-1 text-xs md:text-sm text-foreground-500 line-clamp-2 hidden sm:block">
                        {item.summary}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-black text-foreground-950 mb-6">热播榜单</h2>
              <div className="rounded-lg bg-background-50 border border-background-200 p-2">
                {hot.map((movie, i) => (
                  <Link
                    key={movie.id}
                    to={`/detail/${movie.id}`}
                    className="flex items-center gap-3 p-3 rounded-md hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <span
                      className={`w-7 h-7 shrink-0 rounded-md flex items-center justify-center text-sm font-black ${
                        i === 0 ? "bg-accent-500 text-primary-950" : "bg-secondary-100 text-secondary-800"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-foreground-950 truncate">{movie.title}</p>
                      <p className="text-xs text-foreground-400 truncate">
                        {movie.type} · {movie.genres[0]}
                      </p>
                    </div>
                    <span className="text-sm font-bold text-accent-600 flex items-center gap-0.5">
                      <i className="ri-star-fill text-xs"></i>
                      {movie.rating.toFixed(1)}
                    </span>
                  </Link>
                ))}
              </div>

              <div className="mt-6 rounded-lg bg-primary-900 p-6 text-background-100">
                <h3 className="font-heading text-lg font-bold text-background-50">关于一本道影视大全</h3>
                <p className="mt-3 text-sm leading-relaxed text-primary-200">
                  我们聚合电影与电视剧的丰富资料，提供多维度的分类筛选与及时的行业资讯，
                  帮助每一位影迷更快发现好内容，也让每一次浏览都更轻松愉悦。
                </p>
                <Link
                  to="/category"
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-bold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-compass-3-line"></i>
                  开始探索
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}